import React, { useEffect, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';

// HOOKS
import { useRecordContext } from 'helpers/getRecordData';
import { useSalesStatus } from '../hooks/useSalesStatus';

// GLOBAL FUNCTIONS
import { formatDate } from 'functions.js';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Sprite from 'core/tools/Sprite';

// LOCAL PARTS
import StockNumber from 'routers/interface/tools/StockNumber';

// VARIABLES
const TODAY = new Date();

// MAIN COMPONENT
const BackoutForm = props => {
 
  // FORMIK
  const { values: { aryTradeBackout, backout_in_current_month }, setFieldValue } = useFormikContext() || {};
 
  // HOOKS
  const { record: { has_trade, aryTrade } = {} } = useRecordContext();
  const { currentStatusDate = '', deliveredDate, deliveredinCurrentMonth } = useSalesStatus();

  // MEMOS
  const [
    minBackoutDate,
    backoutDateDisabled
  ] = useMemo(
    () => [
      backout_in_current_month === '1' ? new Date(TODAY.getFullYear(), TODAY.getMonth(), 1) : new Date(deliveredDate),
      !backout_in_current_month
    ],
    [backout_in_current_month, deliveredDate]
  )

  // EFFECTS
  useEffect(
    () => {
      switch (backout_in_current_month) {
        case '0':
          const newDate = new Date(deliveredDate.getFullYear(), deliveredDate.getMonth() + 1, 0);
          setFieldValue('backout_date', formatDate(newDate));
          return;
        case '1':
          setFieldValue('backout_date', formatDate(TODAY));
          return;
        default:
          return;
      };
    },
    [backout_in_current_month, deliveredDate, setFieldValue]
  )

  // RENDER
  return [
    ...(has_trade ? [
      <Form.Col>
        <big><strong className="text-danger">Alert:</strong> You are trying to backout a deal that has a trade. Is the trade being returned to the customer?</big>
      </Form.Col>,
      aryTradeBackout?.map((_trade, i) => [
          <Form.YesNo
            key={i}
            label={`Returned Trade #${i + 1} (${aryTrade[i].stock_num}: ${aryTrade[i].vehicle})`}
            name={`aryTradeBackout[${i}].returned_trade`}
            required
            {...props}
          />,
          parseInt(aryTradeBackout?.[i]?.returned_trade) === 0 ? [
            <Form.Col>
              <big><strong className="text-danger">Alert:</strong> Since the trade is not being returned and will be purchased by the dealer, please enter the new purchase stock number for this vehicle.</big>
            </Form.Col>,
            <StockNumber
              label="Purchase Stock #"
              name={`aryTradeBackout[${i}].purchase_trade_stock_num`}
              required
              {...props}
            />
          ] : null
        ])
    ]: []),
    ...(!deliveredinCurrentMonth ? [
      <Form.Col>
        <big><strong className="text-danger">Alert:</strong> You are trying to backout a deal from a previous month {formatDate(String(currentStatusDate).split('at')[0])}.</big>
      </Form.Col>,
      <Form.YesNo
        name="backout_in_current_month"
        label="Will this Backout be counted in this month?"
        required
        {...props}
      />,
      <Form.Date
        name="backout_date"
        label="BackOut Date"
        minDate={minBackoutDate}
        maxDate={TODAY}
        required
        {...props}
        disabled={backoutDateDisabled}
      />
    ] : []),
    <Form.Textarea
      required
      label="Comments"
      name="backout_comment"
      {...props}
    />,
    <Form.Col>
      <big className="d-flex align-items-center">
        <Sprite use="warning" fill="danger" className="me-2" />
        Please print your Backout form after submission.
      </big>
    </Form.Col>
  ]
};

// EXPORT
export default BackoutForm;
